import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import BackButton from "@/components/BackButton";
import InviteArtistsDialog from "@/components/InviteArtistsDialog";
import { getCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";

const formatPrice = (amount, currency = "USD") => {
  if (!amount || amount <= 0) return "Free";
  const c = currency.toUpperCase();
  const symbol = c === "USD" ? "$" : c === "EUR" ? "€" : c === "GBP" ? "£" : "";
  return `${symbol}${Number(amount).toFixed(2)} / mo`;
};

const ClubDetailPage = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [club, setClub] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeForm, setComposeForm] = useState({ title: "", body: "", tier_required: "" });
  const [posting, setPosting] = useState(false);
  // Phase 19.0 — In-app Invite Artists for clubs (owner/admin only)
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const coverInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);

  const fetchClub = useCallback(async () => {
    try {
      const r = await api.get(`/clubs/${clubId}`);
      setClub(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Club not found");
      navigate("/crews");
    } finally {
      setLoading(false);
    }
  }, [clubId, navigate]);

  const fetchPosts = useCallback(async () => {
    try {
      const r = await api.get(`/clubs/${clubId}/posts`);
      setPosts(r.data?.posts || []);
    } catch (_e) {
      // ignore — likely private club non-sub
    }
  }, [clubId]);

  useEffect(() => { fetchClub(); }, [fetchClub]);
  useEffect(() => { if (club) fetchPosts(); }, [club, fetchPosts]);

  // Poll Stripe checkout status after returning from Stripe (success redirect).
  // MUST live above any early return to respect rules-of-hooks.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled === "1") {
      toast.info("Checkout canceled. You can try again anytime.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sessionId) return;

    let attempts = 0;
    const max = 6;
    const interval = 2000;
    let cancelledFlag = false;
    const tick = async () => {
      if (cancelledFlag) return;
      try {
        const r = await api.get(`/payments/stripe/status/${sessionId}`);
        const { payment_status, status, expires_at } = r.data || {};
        if (payment_status === "paid") {
          toast.success(
            expires_at
              ? `Subscribed! Access until ${new Date(expires_at).toLocaleDateString()}.`
              : "Subscribed!",
          );
          window.history.replaceState({}, "", window.location.pathname);
          const c = await api.get(`/clubs/${clubId}`);
          setClub(c.data);
          fetchPosts();
          return;
        }
        if (status === "expired" || payment_status === "unpaid") {
          toast.error("Payment was not completed.");
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
      } catch (_e) { /* keep polling */ }
      attempts += 1;
      if (attempts < max) setTimeout(tick, interval);
      else {
        toast.info("Still processing. Refresh in a moment.");
        window.history.replaceState({}, "", window.location.pathname);
      }
    };
    tick();
    return () => { cancelledFlag = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white p-8" data-testid="club-loading">
        Loading…
      </div>
    );
  }
  if (!club) return null;

  const isOwner = !!club.is_owner;
  const isPlatformAdmin = me?.role === "admin" || me?.role === "moderator";
  const isSubscriber = !!club.is_subscriber;
  const isMonetized = (club.price_monthly || 0) > 0;
  const tiers = club.tiers || [];

  const handleSubscribe = async () => {
    if (!me) {
      toast.error("Please sign in to subscribe");
      navigate("/login");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      // Paid clubs → Stripe Checkout. Free clubs go through legacy subscribe.
      if (isMonetized) {
        const r = await api.post(`/clubs/${clubId}/checkout`, {
          origin_url: window.location.origin,
        });
        if (r.data?.url) {
          window.location.href = r.data.url;
          return; // browser leaves; no need to unset `busy`
        }
        // Free fallback (shouldn't hit here since isMonetized==true)
      }
      // Free club — instant subscribe, no Stripe round-trip
      const r = await api.post(`/clubs/${clubId}/subscribe`);
      toast.success("Subscribed!");
      setClub((c) => ({
        ...c,
        is_subscriber: !!r.data?.is_subscriber,
        subscriber_count: (c.subscriber_count || 0) + 1,
      }));
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to subscribe");
    } finally {
      setBusy(false);
    }
  };

  // Poll Stripe checkout status after returning from Stripe (success redirect).
  // Moved above early return for React rules-of-hooks compliance.

  const handleManageSubscription = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/users/me/stripe-portal`, {
        return_url: window.location.href,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return; // browser leaves
      }
      toast.error("Could not open Stripe portal");
    } catch (e) {
      const detail = e.response?.data?.detail;
      // 503 from backend = portal not yet configured in Stripe Dashboard.
      // Surface the message + offer the in-app cancel as a fallback.
      toast.error(detail || "Could not open Stripe portal");
    } finally {
      setBusy(false);
    }
  };

  const handleCancelInApp = async () => {
    if (!window.confirm(
      "Cancel your subscription now? You'll lose access to exclusive posts immediately."
    )) return;
    setBusy(true);
    try {
      await api.post(`/clubs/${clubId}/cancel-subscription`);
      toast.success("Subscription canceled");
      setClub((c) => ({
        ...c,
        is_subscriber: false,
        subscriber_count: Math.max(0, (c.subscriber_count || 1) - 1),
      }));
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setBusy(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (busy) return;
    if (!window.confirm("Unsubscribe from this club? You'll lose access to exclusive posts.")) return;
    setBusy(true);
    try {
      await api.post(`/clubs/${clubId}/unsubscribe`);
      toast.success("Unsubscribed");
      setClub((c) => ({ ...c, is_subscriber: false, subscriber_count: Math.max(0, (c.subscriber_count || 1) - 1) }));
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unsubscribe");
    } finally {
      setBusy(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("cover", file);
    try {
      const r = await api.post(`/clubs/${clubId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setClub((c) => ({ ...c, cover_image: r.data.cover_image, cover_updated_at: r.data.cover_updated_at }));
      toast.success("Banner updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Banner upload failed");
    } finally {
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  // Phase C #6: Separate circular avatar (logo) upload
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("avatar", file);
    try {
      const r = await api.post(`/clubs/${clubId}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setClub((c) => ({
        ...c,
        avatar_image: r.data.avatar_image,
        avatar_updated_at: r.data.avatar_updated_at,
      }));
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Avatar upload failed");
    } finally {
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const openEdit = () => {
    setEditForm({
      name: club.name || "",
      description: club.description || "",
      visibility: club.visibility || "public",
      price_monthly: club.price_monthly || 0,
      price_currency: club.price_currency || "USD",
      tiersJson: JSON.stringify(tiers, null, 2),
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async (e) => {
    e?.preventDefault?.();
    if (savingEdit) return;
    let parsedTiers = [];
    try {
      parsedTiers = editForm.tiersJson ? JSON.parse(editForm.tiersJson) : [];
      if (!Array.isArray(parsedTiers)) throw new Error("tiers must be a JSON array");
    } catch (err) {
      toast.error("Tiers JSON is invalid: " + err.message);
      return;
    }
    setSavingEdit(true);
    try {
      const r = await api.patch(`/clubs/${clubId}`, {
        name: editForm.name?.trim(),
        description: (editForm.description || "").trim(),
        visibility: editForm.visibility,
        price_monthly: Number(editForm.price_monthly) || 0,
        price_currency: (editForm.price_currency || "USD").toUpperCase(),
        tiers: parsedTiers,
      });
      setClub(r.data);
      toast.success("Club updated");
      setShowEdit(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCreatePost = async (e) => {
    e?.preventDefault?.();
    if (posting) return;
    if (!composeForm.body?.trim()) {
      toast.error("Body is required");
      return;
    }
    setPosting(true);
    try {
      await api.post(`/clubs/${clubId}/posts`, {
        title: composeForm.title?.trim() || null,
        body: composeForm.body.trim(),
        tier_required: composeForm.tier_required || null,
      });
      toast.success("Posted to your club!");
      setComposeForm({ title: "", body: "", tier_required: "" });
      setShowCompose(false);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post");
    } finally {
      setPosting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    // iter_203 — Context-aware confirm. Owner deleting a subscriber's
    // post (once we add that path) or admin removing gets a stronger
    // wording. Today posts are owner-authored, so "your own" is default.
    const target = (posts || []).find((p) => p.post_id === postId);
    const isOwnPost = target?.owner_id === me?.user_id;
    const msg = isOwnPost
      ? 'Remove this post? It will be replaced with a "Message removed by moderator" tombstone.'
      : 'Remove this entire post and keep its replies for possible restoration?';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/clubs/${clubId}/posts/${postId}`);
      toast.success("Post removed");
      // Reload posts so tombstone renders (soft delete keeps the row)
      try {
        const r = await api.get(`/clubs/${clubId}/posts`);
        setPosts(r.data?.posts || []);
      } catch {
        setPosts((ps) => ps.filter((p) => p.post_id !== postId));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleRestorePost = async (postId) => {
    // iter_203 — Restore a moderator-deleted club post.
    if (!window.confirm("Restore this post? Its original content will reappear for members.")) return;
    try {
      await api.post(`/clubs/${clubId}/posts/${postId}/restore`);
      toast.success("Post restored");
      const r = await api.get(`/clubs/${clubId}/posts`);
      setPosts(r.data?.posts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    }
  };

  const canSeeBody = isOwner || isSubscriber;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="club-detail-page">
      <SharedNavigation currentUser={me} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-28">
        <BackButton fallback="/clubs" testId="club-back-button" alwaysFallback />
      </div>

      {/* Cover hero */}
      <div className="relative">
        <div
          className="h-56 sm:h-72 w-full bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(10,10,15,0.2), rgba(10,10,15,0.95)), url("${getCoverUrl({ cover_url: club.cover_image, cover_updated_at: club.cover_updated_at }, 'cover')}")`,
          }}
          data-testid="club-banner"
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-24 relative z-10 pb-8">
          <div className="flex flex-col sm:flex-row gap-5 items-start sm:items-end">
            <div className="relative">
              <img
                src={getCoverUrl({
                  cover_url: club.avatar_image || club.cover_image,
                  cover_updated_at: club.avatar_updated_at || club.cover_updated_at,
                })}
                alt={club.name}
                className="w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover border-4 border-amber-500/60 shadow-xl shadow-amber-500/10 bg-black"
                data-testid="club-avatar"
              />
              {isOwner && (
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute bottom-1 right-1 bg-black/80 hover:bg-amber-500/30 border border-amber-400/60 text-amber-200 text-[10px] uppercase tracking-wider rounded-full px-2 py-1 backdrop-blur"
                  data-testid="club-change-avatar"
                  title="Change avatar"
                >
                  Edit
                </button>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-amber-300 text-[11px] uppercase tracking-[0.2em] font-bold mb-1">
                🎵 Artist Club{club.visibility === "private" ? " · Private" : ""}
              </p>
              <h1 className="text-3xl sm:text-4xl font-black break-words" data-testid="club-name">{club.name}</h1>
              <div className="mt-2 flex flex-wrap gap-3 items-center text-sm text-gray-300">
                <span data-testid="club-subscriber-count">
                  <strong className="text-white">{club.subscriber_count || 0}</strong> subscriber{club.subscriber_count === 1 ? "" : "s"}
                </span>
                <span className="text-gray-600">•</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                    isMonetized ? "bg-amber-500/20 text-amber-200 border border-amber-500/40" : "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"
                  }`}
                  data-testid="club-price-pill"
                >
                  {formatPrice(club.price_monthly, club.price_currency)}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 self-stretch sm:self-end">
              {/* Phase 18.0 — Share club: copies the public deep-link.
                  Subscription clubs don't use invite-codes (joining is a
                  paid action through Stripe), so the "share" surface is
                  the public landing page URL itself. */}
              <Button
                variant="outline"
                onClick={async () => {
                  const url = `${window.location.origin}/clubs/${club.club_id}`;
                  try {
                    if (navigator.share) {
                      await navigator.share({ title: club.name, text: `Check out ${club.name} on RGM Studio`, url });
                    } else {
                      await navigator.clipboard.writeText(url);
                      toast.success("Club link copied — share it anywhere!");
                    }
                  } catch (e) {
                    if (e?.name !== "AbortError") {
                      // Fallback to clipboard if Web Share fails
                      try {
                        await navigator.clipboard.writeText(url);
                        toast.success("Club link copied!");
                      } catch {
                        toast.error("Couldn't copy — long-press the URL bar to share manually");
                      }
                    }
                  }
                }}
                className="border-amber-400/40 text-amber-100 hover:bg-amber-500/10"
                data-testid="club-share-button"
              >
                🔗 Share
              </Button>
              {!isOwner && !isSubscriber && (
                <Button
                  onClick={handleSubscribe}
                  disabled={busy}
                  className="bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-600 hover:to-pink-600 text-black font-bold disabled:opacity-60"
                  data-testid="club-subscribe-button"
                >
                  {busy ? "…" : isMonetized ? `Subscribe · ${formatPrice(club.price_monthly, club.price_currency)}` : "Follow club"}
                </Button>
              )}
              {!isOwner && isSubscriber && (
                <div className="flex flex-col items-end gap-1">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      disabled
                      className="border-emerald-500/40 text-emerald-200 bg-emerald-500/10"
                      data-testid="club-subscribed-badge"
                    >
                      ✓ Subscribed
                    </Button>
                    {isMonetized ? (
                      <Button
                        variant="outline"
                        onClick={handleManageSubscription}
                        disabled={busy}
                        className="border-purple-500/50 text-purple-200 hover:bg-purple-500/15"
                        data-testid="club-manage-subscription-button"
                        title="Manage payment methods, invoices & cancel via Stripe"
                      >
                        {busy ? "…" : "Manage Subscription"}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={handleUnsubscribe}
                        disabled={busy}
                        className="border-gray-700 text-white hover:bg-white/10"
                        data-testid="club-unsubscribe-button"
                      >
                        {busy ? "…" : "Unfollow"}
                      </Button>
                    )}
                  </div>
                  {isMonetized && (
                    <button
                      type="button"
                      onClick={handleCancelInApp}
                      className="text-[11px] text-gray-500 hover:text-red-300 underline-offset-2 hover:underline"
                      data-testid="club-cancel-in-app-link"
                    >
                      or cancel immediately in-app
                    </button>
                  )}
                </div>
              )}
              {isOwner && (
                <>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleCoverUpload}
                    className="hidden"
                    data-testid="club-cover-input"
                  />
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    data-testid="club-avatar-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => coverInputRef.current?.click()}
                    className="border-gray-700 text-white"
                    data-testid="club-change-cover"
                  >
                    Change Banner
                  </Button>
                  <Button
                    variant="outline"
                    onClick={openEdit}
                    className="border-gray-700 text-white"
                    data-testid="club-edit-button"
                  >
                    ✏️ Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!me) { toast.error("Please sign in"); navigate("/login"); return; }
                      setShowInviteDialog(true);
                    }}
                    className="border-purple-500/40 text-purple-100 hover:bg-purple-500/10"
                    data-testid="club-invite-artists-button"
                  >
                    + Invite Artists
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: feed */}
        <div className="lg:col-span-2 space-y-4">
          {/* About */}
          {club.description && (
            <Card className="p-4 bg-gray-900/40 border-gray-800" data-testid="club-about">
              <h3 className="text-white font-bold mb-2">About</h3>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{club.description}</p>
            </Card>
          )}

          {/* Compose (owner only) */}
          {isOwner && (
            <Card className="p-4 bg-amber-500/5 border-amber-500/30" data-testid="club-compose-card">
              {!showCompose ? (
                <button
                  onClick={() => setShowCompose(true)}
                  className="w-full text-left text-amber-200 hover:text-amber-100 text-sm py-2"
                  data-testid="club-compose-open"
                >
                  ✍️ Post exclusive content for your subscribers…
                </button>
              ) : (
                <form onSubmit={handleCreatePost} className="space-y-3" data-testid="club-compose-form">
                  <Input
                    placeholder="Title (optional)"
                    value={composeForm.title}
                    onChange={(e) => setComposeForm((f) => ({ ...f, title: e.target.value }))}
                    className="bg-black/40 border-gray-700 text-white"
                    data-testid="club-compose-title"
                  />
                  <Textarea
                    placeholder="Share something exclusive: a sneak peek, demo, photo dump, behind the scenes…"
                    value={composeForm.body}
                    onChange={(e) => setComposeForm((f) => ({ ...f, body: e.target.value }))}
                    rows={5}
                    required
                    className="bg-black/40 border-gray-700 text-white"
                    data-testid="club-compose-body"
                  />
                  {tiers.length > 0 && (
                    <select
                      value={composeForm.tier_required}
                      onChange={(e) => setComposeForm((f) => ({ ...f, tier_required: e.target.value }))}
                      className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
                      data-testid="club-compose-tier"
                    >
                      <option value="">Visible to all subscribers</option>
                      {tiers.map((t) => (
                        <option key={t.name} value={t.name}>Restrict to: {t.name}</option>
                      ))}
                    </select>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={posting}
                      className="bg-gradient-to-r from-amber-500 to-pink-500 text-black font-bold disabled:opacity-60"
                      data-testid="club-compose-submit"
                    >
                      {posting ? "Posting…" : "Post"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCompose(false)}
                      className="border-gray-700 text-white"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </Card>
          )}

          {/* Posts feed */}
          <div className="space-y-3" data-testid="club-feed">
            {posts.length === 0 ? (
              <Card className="p-8 bg-gray-900/40 border-gray-800 text-center" data-testid="club-feed-empty">
                <p className="text-gray-400 text-sm">
                  {isOwner
                    ? "No posts yet. Share something exclusive to give subscribers a reason to stick around."
                    : isSubscriber
                    ? "Your artist hasn't posted yet — check back soon!"
                    : "Subscribe to unlock exclusive posts from this artist."}
                </p>
              </Card>
            ) : (
              posts.map((p) => (
                <Card
                  key={p.post_id}
                  className={`p-4 ${
                    p.is_deleted
                      ? "bg-gray-900/20 border-gray-800 opacity-70"
                      : p.is_locked
                      ? "bg-amber-500/5 border-amber-500/20"
                      : "bg-gray-900/40 border-gray-800"
                  }`}
                  data-testid={`club-post-${p.post_id}`}
                >
                  <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
                    <div>
                      {!p.is_deleted && p.title && <h4 className="text-white font-bold">{p.title}</h4>}
                      <p className="text-[11px] text-gray-500">
                        {p.created_at ? new Date(p.created_at).toLocaleString() : ""}
                        {!p.is_deleted && p.tier_required && (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-purple-500/20 border border-purple-500/30 text-purple-200 text-[10px]">
                            {p.tier_required}
                          </span>
                        )}
                      </p>
                    </div>
                    {(isOwner || isPlatformAdmin) && !p.is_deleted && (
                      <button
                        onClick={() => handleDeletePost(p.post_id)}
                        className="text-xs text-gray-500 hover:text-red-400"
                        data-testid={`club-post-delete-${p.post_id}`}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  {p.is_deleted ? (
                    <div className="mt-1 flex items-center gap-2 flex-wrap"
                         data-testid={`club-post-tombstone-${p.post_id}`}>
                      <p className="text-gray-500 italic text-sm">
                        🚫 {p.body || "Message removed by moderator"}
                      </p>
                      {(isOwner || isPlatformAdmin)
                        && p.deleted_by_role && p.deleted_by_role !== "author" && (
                        <button
                          type="button"
                          onClick={() => handleRestorePost(p.post_id)}
                          className="ml-auto text-[11px] font-black text-emerald-300 hover:text-emerald-200 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-full px-2 py-0.5"
                          data-testid={`club-post-restore-${p.post_id}`}
                        >
                          ↩ Restore
                        </button>
                      )}
                    </div>
                  ) : p.is_locked ? (
                    <div className="mt-2" data-testid={`club-post-locked-${p.post_id}`}>
                      <p className="text-gray-400 text-sm italic mb-3">
                        {p.preview ? `${p.preview}` : "Subscriber-only content."}
                      </p>
                      <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/40">
                        <span className="text-2xl">🔒</span>
                        <div className="flex-1">
                          <p className="text-amber-100 font-bold text-sm">Subscribe to unlock</p>
                          <p className="text-amber-200/80 text-[11px]">{formatPrice(club.price_monthly, club.price_currency)}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={handleSubscribe}
                          disabled={busy}
                          className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
                          data-testid={`club-post-subscribe-cta-${p.post_id}`}
                        >
                          Subscribe
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-200 text-sm whitespace-pre-wrap mt-1">{p.body}</p>
                  )}
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Right: tiers + stats */}
        <div className="space-y-4">
          {/* Tiers */}
          <Card className="p-4 bg-gray-900/40 border-gray-800" data-testid="club-tiers">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <span>💎</span> Tiers
            </h3>
            {tiers.length === 0 ? (
              <p className="text-gray-400 text-sm" data-testid="club-tiers-empty">
                {isMonetized
                  ? "Single-tier subscription at " + formatPrice(club.price_monthly, club.price_currency)
                  : isOwner
                  ? "No paid tiers yet — set a monthly price + add tiers in Edit."
                  : "Free club — no paid tiers."}
              </p>
            ) : (
              <div className="space-y-2">
                {tiers.map((t, i) => (
                  <div
                    key={`${t.name}-${i}`}
                    className="p-3 rounded-lg bg-black/30 border border-gray-800"
                    data-testid={`club-tier-${i}`}
                  >
                    <div className="flex items-baseline justify-between mb-1">
                      <h4 className="text-white font-bold text-sm">{t.name || "Tier"}</h4>
                      <span className="text-amber-300 font-bold text-sm">
                        {formatPrice(t.price, club.price_currency)}
                      </span>
                    </div>
                    {Array.isArray(t.benefits) && t.benefits.length > 0 && (
                      <ul className="text-xs text-gray-400 list-disc pl-4 space-y-0.5">
                        {t.benefits.map((b, j) => <li key={j}>{b}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Owner card */}
          <Card className="p-4 bg-gray-900/40 border-gray-800" data-testid="club-owner-card">
            <h3 className="text-white font-bold mb-3 text-sm flex items-center gap-2">
              <span>🎤</span> Artist
            </h3>
            <Link
              to={`/artist/${club.owner_id}`}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-purple-500/10 transition-colors"
              data-testid="club-owner-link"
            >
              <img
                src={getProfilePhotoUrl({ user_id: club.owner_id })}
                alt=""
                className="w-12 h-12 rounded-full object-cover border border-gray-700"
              />
              <div className="min-w-0">
                <p className="text-white font-medium text-sm truncate">View artist profile →</p>
                <p className="text-gray-500 text-[11px] truncate">{club.owner_id}</p>
              </div>
            </Link>
          </Card>

          {/* Manage subscription (placeholder for Step 2 Stripe portal) */}
          {isSubscriber && isMonetized && (
            <Card className="p-4 bg-gray-900/40 border-gray-800" data-testid="club-manage-sub">
              <h3 className="text-white font-bold mb-2 text-sm">Subscription</h3>
              <p className="text-gray-400 text-xs">
                A Stripe customer portal link will appear here once payments are wired (Step 2).
              </p>
            </Card>
          )}
        </div>
      </div>

      {/* Edit modal */}
      {showEdit && isOwner && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => !savingEdit && setShowEdit(false)}
          data-testid="club-edit-dialog"
        >
          <div
            className="w-full max-w-lg bg-[#161620] border border-amber-500/40 rounded-xl p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">✏️ Edit Club</h2>
              <button
                onClick={() => !savingEdit && setShowEdit(false)}
                className="text-gray-400 hover:text-white text-2xl"
                data-testid="close-club-edit"
                aria-label="Close"
              >×</button>
            </div>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <Label className="text-white block mb-1">Name *</Label>
                <input
                  value={editForm.name || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
                  data-testid="club-edit-name"
                />
              </div>
              <div>
                <Label className="text-white block mb-1">Description</Label>
                <textarea
                  value={editForm.description || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
                  data-testid="club-edit-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white block mb-1">Monthly price</Label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.price_monthly ?? 0}
                    onChange={(e) => setEditForm((f) => ({ ...f, price_monthly: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
                    data-testid="club-edit-price"
                  />
                </div>
                <div>
                  <Label className="text-white block mb-1">Currency</Label>
                  <select
                    value={editForm.price_currency || "USD"}
                    onChange={(e) => setEditForm((f) => ({ ...f, price_currency: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
                    data-testid="club-edit-currency"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-white block mb-1">Visibility</Label>
                <select
                  value={editForm.visibility || "public"}
                  onChange={(e) => setEditForm((f) => ({ ...f, visibility: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
                  data-testid="club-edit-visibility"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
              <div>
                <Label className="text-white block mb-1">
                  Tiers (JSON · advanced)
                </Label>
                <textarea
                  value={editForm.tiersJson || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, tiersJson: e.target.value }))}
                  rows={6}
                  className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700 font-mono text-xs"
                  placeholder='[{"name":"Gold","price":9.99,"benefits":["early drops"]}]'
                  data-testid="club-edit-tiers-json"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Format: array of {"{ name, price, benefits[] }"}. Stripe wiring (Step 2) will provision a price per tier.
                </p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={savingEdit}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-pink-500 text-black font-bold disabled:opacity-60"
                  data-testid="club-edit-save"
                >
                  {savingEdit ? "Saving…" : "Save changes"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => !savingEdit && setShowEdit(false)}
                  className="border-gray-700 text-white"
                >Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Phase 19.0 — In-app Invite Artists dialog (owner/admin only) */}
      <InviteArtistsDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        targetType="club"
        clubId={clubId}
        crewName={club?.name || "this club"}
      />
    </div>
  );
};

export default ClubDetailPage;
